import React, { Component } from 'react';
import classNames from 'classnames';
import Popover from '@react/react-spectrum/Popover';
import { List, ListItem } from '@react/react-spectrum/List';
import PropTypes from 'prop-types';
import '../scss/GetHelp.scss';
import DeviceDetectionUtil from '../../utils/DeviceDetectionUtil';
import { interpretKey } from '../../utils/eventUtil';
import { animateScrollToBottomElement } from '../../utils/CSSUtils';

const CLASSNAMES = {
    POPOVER: 'get-help-popover-content',
    DESCRIPTION_SCROLL: 'desc-scroll-section',
    SCROLL_SECTION: 'question-section-scroll',
    FIXED_SECTION: 'question-section-fixed',
    DESCRIPTION_SECTION: 'description-section',
};

/** Padding and max-height of popover content. */
const CSS = {
    PADDING: 42,
    MAX_HEIGHT: 430,
};

export default class GetHelp extends Component {
  /** array for the list ids render in get help menu */
  listRefsId = [];

  /** index of which list is focussed (accessibility) */
  currentListIndex = 0;

  static propTypes = {
      /**
      * config contains question objects (listItems) with headings to
      * be rendered in popover.
      */
      config: PropTypes.arrayOf(PropTypes.shape({
          listId: PropTypes.string,
          heading: PropTypes.string,
          listItems: PropTypes.arrayOf(PropTypes.shape({
              id: PropTypes.string,
              questionText: PropTypes.string,
              answerText: PropTypes.string,
          })),
      })).isRequired,

      /**
      * default item description to be shown on open of popover.
      */
      defaultSelectedItemId: PropTypes.string,

      /**
      * callback called on select on listItem.
      */
      onSelect: PropTypes.func.isRequired,

      /**
      * react component which contains
      * feedback question and its acknowledgement.
      */
      feedback: PropTypes.elementType,

      /**
      * fix last list item on popover
      * the other remaining part under scroll.
      * if false all list under scroll.
      */
      fixLastList: PropTypes.bool,

      /**
      * Focus first element of first list for accessibility
      */
      shouldFocusFirstListItem: PropTypes.bool,

      /**
       * className - Class specific to parent component
       */
      className: PropTypes.string,
  }

  static defaultProps = {
      defaultSelectedItemId: undefined,
      feedback: null,
      fixLastList: true,
      shouldFocusFirstListItem: false,
      className: '',
  }

  state = {
      selectedItemId: this.props.defaultSelectedItemId,
  };

  componentDidMount() {
      requestAnimationFrame(() => {
          this.updateScrollListHeight();
          this.updateDescriptionHeight();
      });
      window.addEventListener('resize', this.windowResizeListener.bind(this));

      const { shouldFocusFirstListItem, defaultSelectedItemId } = this.props;
      if (shouldFocusFirstListItem) {
          const firstList = document.getElementById(this.listRefsId[0]);
          if (firstList && firstList.firstChild) {
              firstList.firstChild.focus();
          }
      }
      this.currentListIndex = 0;
      if (defaultSelectedItemId) {
          const parentDiv = document.getElementsByClassName(CLASSNAMES.SCROLL_SECTION)[0];
          const itemDiv = parentDiv && parentDiv.getElementsByClassName('selectedItem')[0];
          if (parentDiv && itemDiv) {
              animateScrollToBottomElement(50, itemDiv, parentDiv);
          }
      }
  }

  componentDidUpdate(prevProps) {
      const { config } = this.props;
      if (config === prevProps.config) {
          this.updateDescriptionHeight();
      }
  }

  onQuestionClick(listItem) {
      if (listItem.answerText) {
          this.setState({ selectedItemId: listItem.id }, () => {
              const desc = document.getElementsByClassName('description-section')[0];
              if (desc) {
                  desc.focus();
              }
          });
      }
      this.props.onSelect(listItem); // eslint-disable-line
  }

  /** Iterate through config and get matching questionItem. */
  getSelectedItemFromID(selectedItemId) {
      const { config } = this.props;
      let questionItem;
      config.find((list) => {
          const item = list.listItems.find(ques => ques.id === selectedItemId);
          if (item) {
              questionItem = item;
          }
          return item;
      });
      return questionItem;
  }

  /** handles the key events for Pop over menu  */
  handleKeydownPopOver = (event) => {
      const { currentListIndex, listRefsId, state: { selectedItemId } } = this;
      let index;
      if (!selectedItemId) {
          index = event.shiftKey
              ? currentListIndex - 1 + listRefsId.length
              : currentListIndex + 1;
          index %= listRefsId.length;

          const list = document.getElementById(listRefsId[index]);
          if (list && list.firstChild) {
              list.firstChild.focus();
          }

          this.currentListIndex = index;
          event.preventDefault();
      }
  }

  /** Update height of scrollable section of popover */
  updateScrollListHeight() {
      const popoverContainer = document.getElementsByClassName(CLASSNAMES.POPOVER)[0];
      const scrollContainer = document.getElementsByClassName(CLASSNAMES.SCROLL_SECTION)[0];
      const fixedContainer = document.getElementsByClassName(CLASSNAMES.FIXED_SECTION)[0];
      if (popoverContainer && scrollContainer && fixedContainer) {
          const maxHeightString = popoverContainer.style['max-height'] || '0px';
          const popoverMaxHeight = Number(maxHeightString.substring(0, maxHeightString.length - 2));
          if (popoverMaxHeight < CSS.PADDING + CSS.MAX_HEIGHT) {
              scrollContainer.style['max-height'] = `${popoverMaxHeight - fixedContainer.getBoundingClientRect().height - CSS.PADDING}px`;
          } else {
              scrollContainer.style['max-height'] = `${CSS.MAX_HEIGHT - fixedContainer.getBoundingClientRect().height}px`;
          }
      }
  }

  /** Resize event listener with timer to limit callback calls. */
  windowResizeListener() {
      const self = this;
      if (typeof this.timerResize !== 'undefined') {
          clearTimeout(this.timerResize);
      }
      this.timerResize = setTimeout(() => {
          self.updateScrollListHeight();
          self.updateDescriptionHeight();
      }, 10);
  }

  /** Update height of description container relative to popover height */
  updateDescriptionHeight() {
      const popoverContainer = document.getElementsByClassName(CLASSNAMES.POPOVER)[0];
      const descScrollContainer = document.getElementsByClassName(CLASSNAMES.DESCRIPTION_SCROLL)[0];
      const descContainer = document.getElementsByClassName(CLASSNAMES.DESCRIPTION_SECTION)[0];
      if (popoverContainer) {
          this.containerHeight = popoverContainer.offsetHeight - CSS.PADDING;
          if (descContainer) {
              if (DeviceDetectionUtil.isEdge() || DeviceDetectionUtil.isEdgeBeta()) {
                  descContainer.style['min-height'] = '-webkit-fill-available';
              }
          }
          if (descScrollContainer) {
              descScrollContainer.style.height = `${popoverContainer.getBoundingClientRect().height - CSS.PADDING}px`;
              if (DeviceDetectionUtil.isIE()) {
                  descScrollContainer.style['-ms-overflow-style'] = '-ms-autohiding-scrollbar';
              }
          }
      }
  }

  renderDescriptionContent() {
      const { selectedItemId } = this.state;
      const selectedItem = this.getSelectedItemFromID(selectedItemId);
      const { feedback: Feedback } = this.props;
      return (
          <div
              className="description-section"
              aria-label={selectedItem.answerPlainText}
              tabIndex={-1}>
              <div
                  className={CLASSNAMES.DESCRIPTION_SCROLL}
                  style={{ height: this.containerHeight }}>
                  <div className="desc-heading">
                      {selectedItem.questionText}
                  </div>
                  {/* eslint-disable react/no-danger */}
                  <div className="desc-content" dangerouslySetInnerHTML={{ __html: selectedItem.answerText }} />
                  {Feedback && <Feedback question={selectedItem.id} />}
              </div>
          </div>
      );
  }

  renderList(list) {
      const { selectedItemId } = this.state;
      if (this.listRefsId.indexOf(list.listId) === -1) {
          this.listRefsId.push(list.listId);
      }
      return (
          <div className="question-section" key={list.listId}>
              <div className="list-heading">
                  {list.heading}
              </div>
              <List className="list-section" id={list.listId}>
                  {list.listItems.map((listItem, index) => (
                      <ListItem
                          key={listItem.id}
                          className={classNames('list-Item', (selectedItemId === listItem.id ? 'selectedItem' : ''))}
                          aria-label={listItem.questionText}
                          tabIndex={0}
                          onSelect={() => this.onQuestionClick(listItem)}>
                          {listItem.questionText}
                      </ListItem>
                  ))}
              </List>
          </div>
      );
  }

  renderListContent() {
      const { config, fixLastList } = this.props;
      const scrollLists = [...config];
      let lastList;
      if (scrollLists.length > 1 && fixLastList && window.innerHeight > 300) {
          lastList = scrollLists.pop();
      }
      return (
          <div className={classNames('questions-list-container', (lastList ? 'with-fixed-list' : ''))}>
              <div className={CLASSNAMES.SCROLL_SECTION}>
                  {scrollLists.map(list => this.renderList(list))}
              </div>
              <div className={CLASSNAMES.FIXED_SECTION}>
                  {lastList ? this.renderList(lastList) : null}
              </div>
          </div>
      );
  }

  render() {
      const { selectedItemId } = this.state;
      const showDescription = !!this.getSelectedItemFromID(selectedItemId);
      const {
          config, defaultSelectedItemId, onSelect, feedback, className, ...restProps
      } = this.props;
      const handleKeydown = interpretKey({ onTab: [this.handleKeydownPopOver, false, false] });
      return (
          <Popover
              {...restProps}
              trapFocus={false}
              className={classNames(CLASSNAMES.POPOVER, className, (showDescription ? 'gh-with-description' : ''))}
              onKeyDown={handleKeydown}>
              {showDescription && this.renderDescriptionContent()}
              {this.renderListContent()}
          </Popover>
      );
  }
}
